import React, { useState } from 'react';

const RealityCheck = ({ plan, onOptionSelect }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const rc = plan?.reality_check;

  if (!rc || !rc.triggered) return null;

  const cp = plan.credit_profile || {};
  const cs = cp.score || plan.current_score || 620;
  const gs = cp.goal_score || plan.goal_score || 720;
  const tm = cp.timeline_months || plan.timeline_months || 6;
  const mb = cp.monthly_budget || plan.monthly_budget || 300;
  const ranges = rc.realistic_ranges || {};

  const handleSelect = (option) => {
    setSelectedOption(option);
    if (option === 'fastest') {
      // Scroll to the action plan section
      setTimeout(() => {
        const planEl = document.querySelector('[data-testid="context-label"]');
        if (planEl) planEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
    if (onOptionSelect) onOptionSelect(option);
  };

  if (selectedOption === 'fastest') return null;

  return (
    <div className="rc-wrap" data-testid="reality-check">
      <div className="rc-header">
        <div className="rc-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 9v4m0 4h.01M12 3l9.5 16.5H2.5L12 3z" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        <h2 className="rc-title">Let's align your goal with what's realistically achievable</h2>
      </div>

      <p className="rc-body">
        Based on your current credit profile, reaching a <strong>{gs}</strong> score
        in <strong>{tm} months</strong> with a <strong>${mb}/month</strong> budget
        is ambitious. Here's what's realistically possible.
      </p>

      {/* Realistic Ranges */}
      <div className="rc-ranges" data-testid="rc-ranges">
        <h3 className="rc-ranges-title">Here's what's possible instead</h3>
        {ranges.short_term && (
          <div className="rc-range-row">
            <span className="rc-range-label">{ranges.short_term.label}</span>
            <span className="rc-range-value">{ranges.short_term.low} — {ranges.short_term.high}</span>
          </div>
        )}
        {ranges.mid_term && (
          <div className="rc-range-row">
            <span className="rc-range-label">{ranges.mid_term.label}</span>
            <span className="rc-range-value">{ranges.mid_term.low} — {ranges.mid_term.high}</span>
          </div>
        )}
        {ranges.long_term && (
          <div className="rc-range-row">
            <span className="rc-range-label">{ranges.long_term.label}</span>
            <span className="rc-range-value">{ranges.long_term.low} — {ranges.long_term.high}</span>
          </div>
        )}
        <div className="rc-range-current">
          <span>Your current score: <strong>{cs}</strong></span>
          <span>Achievable maximum: <strong>{rc.achievable_max}</strong></span>
        </div>
      </div>

      {/* 3 Options */}
      <div className="rc-options" data-testid="rc-options">
        <button
          className={`rc-option ${selectedOption === 'fastest' ? 'rc-option-selected' : ''}`}
          onClick={() => handleSelect('fastest')}
          data-testid="rc-option-fastest"
        >
          <div className="rc-option-badge">RECOMMENDED</div>
          <h4>Start My Fastest Score Boost Plan</h4>
          <p>Follow the most effective steps to increase your score as quickly as possible with your current budget.</p>
        </button>

        <button
          className={`rc-option ${selectedOption === 'adjust' ? 'rc-option-selected' : ''}`}
          onClick={() => handleSelect('adjust')}
          data-testid="rc-option-adjust"
        >
          <h4>Adjust My Budget to Reach My Goal Faster</h4>
          <p>See how increasing your monthly payment can improve your timeline and results.</p>
        </button>

        <button
          className={`rc-option ${selectedOption === 'breakdown' ? 'rc-option-selected' : ''}`}
          onClick={() => handleSelect('breakdown')}
          data-testid="rc-option-breakdown"
        >
          <h4>See What It Takes to Reach My Goal</h4>
          <p>View the estimated time, actions, and financial impact required to reach your target score.</p>
        </button>
      </div>

      {/* Option 2: Budget Adjust Placeholder */}
      {selectedOption === 'adjust' && (
        <div className="rc-adjust-preview" data-testid="rc-adjust-preview">
          <h4>Budget Impact Preview</h4>
          <div className="rc-adjust-rows">
            <div className="rc-adjust-row">
              <span>${mb}/mo (current)</span>
              <span>+{rc.realistic_ranges?.mid_term?.high ? rc.realistic_ranges.mid_term.high - cs : '—'} pts in 6 months</span>
            </div>
            <div className="rc-adjust-row">
              <span>${Math.round(mb * 1.5)}/mo</span>
              <span>+{Math.round((rc.realistic_ranges?.mid_term?.high - cs) * 1.25) || '—'} pts in 6 months</span>
            </div>
            <div className="rc-adjust-row">
              <span>${mb * 2}/mo</span>
              <span>+{Math.round((rc.realistic_ranges?.mid_term?.high - cs) * 1.45) || '—'} pts in 6 months</span>
            </div>
          </div>
          <button className="rc-proceed-btn" onClick={() => handleSelect('fastest')}>
            Continue With Current Budget
          </button>
        </div>
      )}

      {/* Option 3: Breakdown */}
      {selectedOption === 'breakdown' && (
        <div className="rc-breakdown" data-testid="rc-breakdown">
          <h4>What It Takes to Reach {gs}</h4>
          <div className="rc-breakdown-items">
            <div className="rc-breakdown-item">
              <span className="rc-bd-label">Points Needed</span>
              <span className="rc-bd-value">+{gs - cs} pts</span>
            </div>
            <div className="rc-breakdown-item">
              <span className="rc-bd-label">Estimated Timeline</span>
              <span className="rc-bd-value">{gs - cs > 150 ? '12-18' : gs - cs > 80 ? '8-12' : '4-8'} months</span>
            </div>
            <div className="rc-breakdown-item">
              <span className="rc-bd-label">Recommended Budget</span>
              <span className="rc-bd-value">${Math.max(mb, Math.round((gs - cs) * 3))}/mo</span>
            </div>
            {rc.profile_flags?.has_collections && (
              <div className="rc-breakdown-item rc-bd-warning">
                <span className="rc-bd-label">Collections</span>
                <span className="rc-bd-value">Must be addressed first</span>
              </div>
            )}
          </div>
          <button className="rc-proceed-btn" onClick={() => handleSelect('fastest')}>
            Start My Plan Now
          </button>
        </div>
      )}
    </div>
  );
};

export default RealityCheck;
